/**
 * Referencia personal del asociado para cobranza (ADR 024 · migración 0400).
 * La deja el asociado al actualizar sus datos; sirve para ubicarlo cuando no responde.
 * Llamar / WhatsApp a la referencia queda registrado como gestión (onGestion).
 */
import { useEffect, useState } from 'react'
import { Phone, WhatsappLogo, UsersThree } from '@phosphor-icons/react'
import { supabase } from '@/lib/supabase'

type Ref = { id: string; nombre: string; telefono: string | null; relacion: string | null; created_at: string }

const soloDigitos = (t: string) => t.replace(/\D/g, '')

export function ReferenciasCobranza({ asociadoId, creditoId, nombreAsociado, onGestion, plegable = false }: {
  asociadoId?: string; creditoId?: string; nombreAsociado: string; onGestion?: (nota: string) => void
  /** En listas largas: muestra un botón y recién consulta al tocarlo. */
  plegable?: boolean
}) {
  const [abierto, setAbierto] = useState(!plegable)
  const [refs, setRefs] = useState<Ref[] | null>(null)
  useEffect(() => {
    if (!abierto) return
    let vivo = true
    const q = creditoId
      ? supabase.rpc('referencias_cobranza_credito', { p_credito_id: creditoId })
      : supabase.rpc('referencias_cobranza', { p_asociado_id: asociadoId })
    q.then(({ data }) => { if (vivo) setRefs((data ?? []) as Ref[]) })
    return () => { vivo = false }
  }, [abierto, asociadoId, creditoId])

  if (!abierto) {
    return (
      <button onClick={() => setAbierto(true)} className="btn btn-ghost"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, marginTop: 8 }}>
        <UsersThree size={14} weight="bold" /> Ver referencia personal
      </button>
    )
  }
  if (refs === null) return null
  const primerNombre = nombreAsociado.split(' ')[0] ?? ''
  return (
    <div style={{ border: '1px solid var(--hairline)', borderRadius: 10, padding: '12px 14px', marginBottom: 16 }}>
      <p style={{ margin: 0, fontSize: 11, fontWeight: 800, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 1.2, display: 'flex', alignItems: 'center', gap: 6 }}>
        <UsersThree size={14} weight="bold" /> Referencia personal
      </p>
      {refs.length === 0 ? (
        <p style={{ margin: '8px 0 0', fontSize: 12, color: 'var(--muted-foreground)' }}>Todavía no dejó referencia (se pide en la actualización de datos).</p>
      ) : refs.map((r) => {
        const num = r.telefono ? soloDigitos(r.telefono) : ''
        const wa = num.length >= 8 ? `https://wa.me/${num}?text=${encodeURIComponent(`Hola, ${r.nombre.split(' ')[0]}. Te escribimos de Neocoop: ${primerNombre} te dejó como su referencia personal y no hemos logrado comunicarnos. ¿Nos podrías ayudar a que se comunique con nosotros? Gracias.`)}` : null
        return (
          <div key={r.id} style={{ marginTop: 10 }}>
            <p style={{ margin: 0, fontSize: 13, fontWeight: 700 }}>{r.nombre}</p>
            <p style={{ margin: '2px 0 8px', fontSize: 12, color: 'var(--muted-foreground)' }}>
              {r.relacion ?? '—'} · <span style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{r.telefono ?? 'sin teléfono'}</span>
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              {r.telefono && (
                <a href={`tel:${r.telefono}`} onClick={() => onGestion?.(`Llamada a referencia personal (${r.relacion ?? ''})`)} className="btn btn-ghost"
                  style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 12, fontWeight: 700 }}>
                  <Phone size={14} weight="bold" /> Llamar
                </a>
              )}
              {wa && (
                <a href={wa} target="_blank" rel="noreferrer" onClick={() => onGestion?.(`WhatsApp a referencia personal (${r.relacion ?? ''})`)}
                  style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '8px 10px', borderRadius: 8, background: '#25D366', color: '#fff', fontSize: 12, fontWeight: 700, textDecoration: 'none' }}>
                  <WhatsappLogo size={14} weight="fill" /> WhatsApp
                </a>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
